import { useState, useMemo, FormEvent, useCallback, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductListEditor } from "./ProductListEditor";
import { DeliveryDatePicker } from "./DeliveryDatePicker";
import { OverlaySection, OverlaySectionGroup } from "@/components/layout/OverlaySection";
import { InlineSummaryRow, FieldStack } from "@/components/sheet";
import { useToast } from "@/hooks/use-toast";
import { useOrderMutations } from "@/hooks/useOrderMutations";
import { ORDER_CATEGORIES, ORDER_SOURCES, ORDER_SOURCE, CURRENCY } from "@/lib/constants";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryLabel, getSourceLabel } from "@/translations";
import { manualOrderSchema } from "@/lib/validationSchemas";
import { useUserSettings, DEFAULT_ORDER_ID_CONFIG } from "@/hooks/useUserSettings";
import { getOrderIdPreview } from "@/lib/orderNumberGenerator";

interface Product {
  name: string;
  quantity: string;
  unit: string;
  price: string;
}

interface ManualOrderFormProps {
  onOrderCreated: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * ManualOrderForm - Products-First Layout (v2)
 * 
 * UX Spec:
 * - Products come FIRST (dominant visual zone)
 * - Customer & Delivery second
 * - Order Metadata third (compact, auto-fill friendly)
 * - Additional Details collapsed by default
 * - Reduced pilliness, denser layout for 400px sheet
 */
export const ManualOrderForm = ({ onOrderCreated, onCancel, onDirtyChange, onLoadingChange }: ManualOrderFormProps) => {
  // Form state
  const [orderNumber, setOrderNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [address, setAddress] = useState("");
  const [message, setMessage] = useState("");
  const [source, setSource] = useState<string>(ORDER_SOURCE.MANUAL);
  const [category, setCategory] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [comments, setComments] = useState("");
  const [csrName, setCsrName] = useState("");
  // Initialize with one empty product row for immediate entry
  const [products, setProducts] = useState<Product[]>([{ name: "", quantity: "", unit: "ცალი", price: "" }]);
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Dirty state tracking
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Ref for first product input (products-first focus)
  const firstProductInputRef = useRef<HTMLInputElement>(null);

  // Notify parent of dirty state changes
  const isDirty = changedFields.size > 0;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Notify parent of loading state for overlay action bar
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Auto-focus first product input on mount (products-first UX)
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      firstProductInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  const { toast } = useToast();
  const { language, t } = useLanguage();
  const { checkDuplicateOrder, createOrder } = useOrderMutations();
  const { data: userSettings } = useUserSettings();

  // Auto-generate preview for order number and auto-fill (point #4)
  const autoGeneratePreview = useMemo(() => {
    const config = userSettings?.orderIdConfig || DEFAULT_ORDER_ID_CONFIG;
    return getOrderIdPreview(config, 'manual');
  }, [userSettings?.orderIdConfig]);
  
  // Auto-fill order number with generated value on mount (point #4)
  useEffect(() => {
    if (!orderNumber && autoGeneratePreview) {
      setOrderNumber(autoGeneratePreview);
    }
  }, [autoGeneratePreview]);

  // Track field changes
  const trackFieldChange = useCallback((fieldName: string, value: any) => {
    setChangedFields((prev) => new Set(prev).add(fieldName));
    // Clear validation error for this field when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  }, [validationErrors]);

  const validateForm = (): boolean => {
    try {
      manualOrderSchema.parse({
        orderNumber,
        customerName,
        customerPhone,
        address,
        message,
        source,
        category,
        totalPrice,
        comments,
        csrName,
      });
      setValidationErrors({});
      return true;
    } catch (error: any) {
      const newErrors: Record<string, string> = {};
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          const fieldName = err.path[0];
          newErrors[fieldName] = err.message;
        });
        setValidationErrors(newErrors);

        // Show toast for first error
        const firstError = error.errors[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
      }
      return false;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Check for duplicate order number if provided
    if (orderNumber) {
      const isDuplicate = await checkDuplicateOrder(orderNumber);
      if (isDuplicate) {
        toast({
          title: t("orders.duplicate.title"),
          description: t("orders.duplicate.description").replace("{{orderNumber}}", orderNumber),
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);
    try {
      // Filter out empty products before saving
      const validProducts = products.filter(p => p.name.trim() !== "");
      
      await createOrder({
        orderNumber,
        customerName,
        customerPhone,
        address,
        message,
        source,
        category,
        totalPrice,
        comments,
        csrName,
        parsedItems: validProducts.length > 0 ? JSON.stringify(validProducts) : undefined,
        requestedDeliveryDate,
      });

      toast({
        title: t("orders.success.extractionSuccess"),
        description: t("orders.success.orderCreated"),
      });

      // Clear dirty state on successful save
      setChangedFields(new Set());
      onOrderCreated();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle products change with auto-calculate total (point #2)
  const handleProductsChange = useCallback((newProducts: Product[]) => {
    setProducts(newProducts);
    trackFieldChange("products", newProducts);
    
    // Auto-calculate total from products
    const calculatedTotal = newProducts.reduce((sum, p) => {
      const price = parseFloat(p.price) || 0;
      const qty = parseFloat(p.quantity) || 1;
      return sum + (price * qty);
    }, 0);
    
    if (calculatedTotal > 0) {
      setTotalPrice(calculatedTotal.toFixed(2));
      trackFieldChange("totalPrice", calculatedTotal.toFixed(2));
    }
  }, [trackFieldChange]);

  return (
    <form onSubmit={handleSubmit} className="min-w-0">
      <OverlaySectionGroup>
        {/* Section 1: PRODUCTS - No header, products list directly */}
        <OverlaySection>
          <ProductListEditor 
            products={products} 
            onProductsChange={handleProductsChange}
            firstInputRef={firstProductInputRef}
          />

          {/* Total Price - using InlineSummaryRow primitive */}
          <InlineSummaryRow
            label={`${t("orders.labels.totalPrice")} (${CURRENCY.symbol})`}
            value={
              <Input
                id="totalPrice"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={totalPrice}
                onChange={(e) => {
                  setTotalPrice(e.target.value);
                  trackFieldChange("totalPrice", e.target.value);
                }}
                disabled={loading}
                className="w-28 text-right font-medium h-8 text-sm"
                aria-invalid={!!validationErrors.totalPrice}
              />
            }
            showBorder
          />
          {validationErrors.totalPrice && (
            <p className="text-xs text-destructive text-right">{validationErrors.totalPrice}</p>
          )}
        </OverlaySection>

        {/* Section 2: Customer & Delivery */}
        <OverlaySection title={t("orders.detail.sectionCustomer")}>
          <div className="space-y-3">
            <FieldStack label={t("orders.labels.customerName")} htmlFor="customerName" required error={validationErrors.customerName}>
              <Input
                id="customerName"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  trackFieldChange("customerName", e.target.value);
                }}
                placeholder={t("orders.placeholders.customerName")}
                disabled={loading}
              />
            </FieldStack>

            <FieldStack label={t("orders.labels.customerPhone")} htmlFor="customerPhone">
              <Input
                id="customerPhone"
                placeholder={t("orders.placeholders.customerPhone")}
                value={customerPhone}
                onChange={(e) => {
                  setCustomerPhone(e.target.value);
                  trackFieldChange("customerPhone", e.target.value);
                }}
                disabled={loading}
              />
            </FieldStack>

            <FieldStack label={t("orders.labels.deliveryAddressSimple")} htmlFor="address">
              <Input
                id="address"
                placeholder={t("orders.placeholders.address")}
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  trackFieldChange("address", e.target.value);
                }}
                disabled={loading}
              />
            </FieldStack>

            <FieldStack label={t("orders.labels.requestedDeliveryDate") || "Delivery Date"} htmlFor="requestedDeliveryDate">
              <DeliveryDatePicker
                value={requestedDeliveryDate}
                onChange={(date) => {
                  setRequestedDeliveryDate(date);
                  trackFieldChange("requestedDeliveryDate", date || "");
                }}
                disabled={loading}
                includeTime={true}
              />
            </FieldStack>
          </div>
        </OverlaySection>

        {/* Section 3: Order Metadata - No header */}
        <OverlaySection title={t("orders.detail.sectionMetadata")}>
          <div className="space-y-3">
            <FieldStack label={t("orders.labels.orderNumber")} htmlFor="orderNumber" error={validationErrors.orderNumber}>
              <Input
                id="orderNumber"
                placeholder={autoGeneratePreview}
                value={orderNumber}
                onChange={(e) => {
                  setOrderNumber(e.target.value);
                  trackFieldChange("orderNumber", e.target.value);
                }}
                disabled={loading}
                aria-invalid={!!validationErrors.orderNumber}
              />
            </FieldStack>

            <FieldStack label={t("orders.labels.category")} htmlFor="category" required error={validationErrors.category}>
              <Select 
                value={category} 
                onValueChange={(value) => {
                  setCategory(value);
                  trackFieldChange("category", value);
                }} 
                disabled={loading}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder={t("orders.placeholders.selectCategory")} />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {getCategoryLabel(cat, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldStack>

            <FieldStack label={t("orders.labels.source")} htmlFor="source">
              <Select 
                value={source} 
                onValueChange={(value) => {
                  setSource(value);
                  trackFieldChange("source", value);
                }} 
                disabled={loading}
              >
                <SelectTrigger id="source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_SOURCES.map((src) => (
                    <SelectItem key={src} value={src}>
                      {getSourceLabel(src, language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldStack>
          </div>
        </OverlaySection>

        {/* Section 4: Additional Details - Collapsed by default */}
        <OverlaySection 
          title={t("orders.labels.moreDetails") || "+ More order details"}
          collapsible 
          defaultOpen={false}
        >
          <div className="space-y-3">
            <FieldStack label={t("orders.labels.csrName")} htmlFor="csrName">
              <Input
                id="csrName"
                placeholder={t("orders.placeholders.csrName")}
                value={csrName}
                onChange={(e) => {
                  setCsrName(e.target.value);
                  trackFieldChange("csrName", e.target.value);
                }}
                disabled={loading}
              />
            </FieldStack>

            <FieldStack label={t("orders.labels.orderMessage")} htmlFor="message">
              <Textarea
                id="message"
                placeholder={t("orders.placeholders.orderMessage")}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  trackFieldChange("message", e.target.value);
                }}
                disabled={loading}
                className="min-h-[80px]"
              />
            </FieldStack>

            <FieldStack label={t("orders.labels.commentsSpecial")} htmlFor="comments">
              <Textarea
                id="comments"
                placeholder={t("orders.placeholders.comments")}
                value={comments}
                onChange={(e) => {
                  setComments(e.target.value);
                  trackFieldChange("comments", e.target.value);
                }}
                disabled={loading}
                className="min-h-[80px]"
              />
            </FieldStack>
          </div>
        </OverlaySection>
      </OverlaySectionGroup>
    </form>
  );
};
